import { DB_TYPE } from '@/common/define/common.define';
import {
  getDbSinglestoreFullTextByInstanceId,
  getGetDbSinglestoreFullTextByInstanceIdQueryKey,
} from '@/api-client/singlestore/queries/single-store-cmmn-controller/single-store-cmmn-controller';
import { FRAME_NAMES } from '@/common/define/apiTrace.define';
import { DATABASE_DETAIL_TYPE, DOCUMENT_TEXT_TYPE } from '@/database/adapter/openDetail.define';
import type { DocumentTextParams, ParameterDetailParams, SqlDetailParams } from '../types';
import { formatName, mergeDetailInfo, parseSchema } from './utils';
import { getRequiredMap } from '@/common/utils/core/detailFieldMeta.utils';
import {
  sqlDetailSchema,
  parameterDetailSchema,
  documentTextSchema,
  type SqlDetailSchema,
  type SinglestoreSqlDetailAddition,
  type ParameterDetailSchema,
  type SinglestoreParameterDetailAddition,
  type DocumentTextSchema,
} from './singlestore.schema';

export const getSingleStoreSqlDetailAddition = ({
  detailInfo,
  fromTime,
  toTime,
  instanceId,
}: SqlDetailParams): { name: string; addition: SinglestoreSqlDetailAddition } => {
  const { data } = parseSchema<SqlDetailSchema>(
    sqlDetailSchema,
    mergeDetailInfo(detailInfo, instanceId, { fromTime, toTime }),
    DATABASE_DETAIL_TYPE.SQL,
  );

  return {
    name: formatName(data.aggregatorActivityName),
    addition: {
      dbType: DB_TYPE.SINGLESTORE,
      instanceId: data.instanceId,
      fromTime: data.fromTime,
      toTime: data.toTime,
      aggregatorActivityName: data.aggregatorActivityName,
    },
  };
};

export const getSinglestoreParameterDetailAddition = ({
  detailInfo,
  fromTime,
  toTime,
  instanceId,
}: ParameterDetailParams): { name: string; addition: SinglestoreParameterDetailAddition } => {
  const { data } = parseSchema<ParameterDetailSchema>(
    parameterDetailSchema,
    mergeDetailInfo(detailInfo, instanceId, { fromTime, toTime }),
    DATABASE_DETAIL_TYPE.PARAMETER,
  );

  return {
    name: formatName(data.parameterName),
    addition: {
      dbType: DB_TYPE.SINGLESTORE,
      instanceId: data.instanceId,
      fromTime: data.fromTime,
      toTime: data.toTime,
    },
  };
};

export const getSinglestoreDocumentTextParams = ({
  detailInfo,
  instanceId,
  useFieldValidation,
}: DocumentTextParams) => {
  if (!useFieldValidation) {
    return {
      instanceId,
      activityName: detailInfo.aggregatorActivityName ?? detailInfo.activityName,
      sqlTextHashId: detailInfo.sqlTextHashId,
    };
  }

  const { data } = parseSchema<DocumentTextSchema>(
    documentTextSchema,
    mergeDetailInfo(detailInfo, instanceId),
    DOCUMENT_TEXT_TYPE,
  );

  return {
    instanceId: data.instanceId,
    activityName: data.activityName,
    sqlTextHashId: data.sqlTextHashId,
  };
};

export const fetchSinglestoreDocumentText = async ({
  frameName,
  abortController,
  ...rest
}: DocumentTextParams) => {
  const params = getSinglestoreDocumentTextParams({ ...rest, useFieldValidation: true });

  const {
    data: { data = [] },
  } = await getDbSinglestoreFullTextByInstanceId(
    params.instanceId!,
    {
      activityName: params.activityName,
      sqlTextHashId: params.sqlTextHashId,
    },
    { params: { frameName: frameName || FRAME_NAMES.DOCUMENT_WINDOW.FULL_TEXT } },
    abortController?.signal,
  );

  return data[0] || null;
};

export const getSinglestoreDocumentTextQueryKey = (params: DocumentTextParams | null) => {
  if (!params) {
    return getGetDbSinglestoreFullTextByInstanceIdQueryKey();
  }

  const convertedParams = getSinglestoreDocumentTextParams({
    ...params,
    useFieldValidation: false,
  });

  return getGetDbSinglestoreFullTextByInstanceIdQueryKey(
    convertedParams.instanceId,
    convertedParams,
  );
};

export const singlestoreSqlDetailRequiredMap = getRequiredMap(sqlDetailSchema);
export const singlestoreDocumentTextRequiredMap = getRequiredMap(documentTextSchema);
